import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const DeleteAccountDialog = ({ open, onOpenChange, reauthed = false }) => {
  const { user, deleteAccount, reauthWithGoogleForDeletion } = useAuth();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);
  const method = user?.auth_method || 'email';
  const isEmail = method === 'email';
  const isGoogle = method === 'google';
  const ready = confirmation === 'DELETE' && (!isEmail || password.length > 0);

  useEffect(() => {
    if (open) setNeedsReauth(isGoogle && !reauthed);
  }, [open, isGoogle, reauthed]);

  const reset = () => { setConfirmation(''); setPassword(''); };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteAccount(confirmation, isEmail ? password : undefined);
      toast.success('Your account has been deleted');
      onOpenChange(false);
      reset();
      navigate('/login');
    } catch (err) {
      if (err?.code === 'reauth_required') {
        setNeedsReauth(true);
        toast.error(err.message);
      } else {
        toast.error(typeof err === 'string' ? err : 'Account deletion failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" /> Delete your account
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 text-sm text-left">
              <p>This permanently signs you out everywhere and removes:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Connected brokerage credentials (Schwab, Public)</li>
                <li>Your synced portfolio data and all sessions</li>
                <li>Your name and email from your profile</li>
              </ul>
              <p>
                Any active subscription is cancelled at the end of its current billing period; you will not be
                charged again. Billing records are retained as required by law.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        {needsReauth ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2" data-testid="delete-account-reauth-notice">
            <p className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
              For your security, please sign in with Google again. You&apos;ll be brought straight back here to finish.
            </p>
            <Button onClick={reauthWithGoogleForDeletion} className="w-full" data-testid="delete-account-reauth-btn">
              Sign in with Google to continue
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Type <span className="font-mono font-bold">DELETE</span> to confirm</label>
              <Input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                data-testid="delete-account-confirmation-input"
              />
            </div>
            {isEmail && (
              <div>
                <label className="text-xs font-medium text-slate-600">Your password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="delete-account-password-input"
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="delete-account-cancel-btn">
            Keep my account
          </Button>
          {!needsReauth && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!ready || busy}
              data-testid="delete-account-submit-btn"
            >
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete account
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountDialog;
